import { Button, Card, Drawer, Grid, Spinner } from '@design-systems/canvas-ui'
import Icon, {
  TimeHistoryIcon,
  InfoIcon,
  PencilIcon,
} from '@design-systems/react-icons'
import { isEmpty } from 'lodash'
import { CustomTooltip } from 'mvscommoncomponents'
import { useMemo } from 'react'
import { STATUS_RFI3_APPROVED } from '../../../../common/commonConstants'
import CapabilityAuditTrail from '../../../../globalComponents/RfiCommons/CapabilityAuditTrail'

export default function RFI3ProductCapabilitiesApprovalCards({
  rfi3Template,
  setProductCapabilityDialogSettings,
  productCapabilityDialogSettings,
  rfiType,
  capabilityList,
  isLoading = false,
  onViewHistory,
  isReadonly,
  manufacturingTypeId,
  auditData,
  isCapabilityAuditOpen,
  supplierId,
  locationId,
  onCapabilityAuditDrawerClose,
}) {
  const hasEverRFIApproved = useMemo(() => {
    const list = auditData || []

    return list.some(
      (auditItem) =>
        (auditItem.vetting_status || '').toUpperCase() === STATUS_RFI3_APPROVED,
    )
  }, [auditData])

  if (!isReadonly) {
    return null
  }

  if (capabilityList === undefined || isEmpty(capabilityList)) {
    return null
  }

  const {
    total_capability = {
      total_capabilities: 0,
      total_approved: 0,
    },
    approved_capability_list = [],
  } = capabilityList

  if (isLoading) {
    return (
      <Card
        variant="elevated"
        className="hc8-mb-3x hc8-pa-3x"
        data-testid="product-capabilities-approval-cards-loading"
      >
        <div className="hc8-ta-center hc8-pa-6x">
          <Spinner size="md" />

          <div className="hc8-fs-100 hc8-clr-disabled hc8-mt-3x">
            Loading approved capabilities...
          </div>
        </div>
      </Card>
    )
  }

  const formatCapabilityLine = (cap) => {
    return `${cap.primary_type || ''} ${
      cap.secondary_type ? `- ${cap.secondary_type}` : ''
    }${cap.tertiary_type ? ` - ${cap.tertiary_type}` : ''}`
  }

  const buildTooltipContent = (caps = []) => (
    <div className="lcm-capability-tooltip">
      {caps.map((c, idx) => (
        <div key={idx} className="lcm-capability-tooltip__line">
          {formatCapabilityLine(c)}
        </div>
      ))}
    </div>
  )

  const percentBase = total_capability?.total_capabilities ?? 0

  return (
    <Card
      variant="border"
      className="lcm-product-capability-approval hc8-mb-3x hc8-pa-4x"
      data-testid="product-capabilities-approval-cards"
    >
      <Grid.Container spacing="normal">
        <Grid.Item size={{ xs: 8 }} className="hc8-pb-none">
          <div className="lcm-product-capability-approval__title hc8-fs-200 hc8-mb-3x">
            Product Capabilities Approval
          </div>

          <div className="hc8-fs-150 hc8-clr-disabled hc8-mb-2x">
            Manage approved capabilities across Target Designated and Vendor
            Sourced
          </div>
        </Grid.Item>

        <Grid.Item
          size={{ xs: 4 }}
          className="lcm-product-capability-approval__actions"
        >
          {hasEverRFIApproved && (
            <Button
              variant="secondary"
              size="md"
              className="hc8-mr-3x"
              onClick={onViewHistory}
              data-testid="view-history-button"
            >
              <Icon
                svg={TimeHistoryIcon}
                size="inline"
                className="hc8-mr-1x"
                aria-hidden
              />
              View History
            </Button>
          )}

          {rfi3Template?.show_approval_popup && hasEverRFIApproved ? (
            <Button
              variant="primary"
              size="md"
              className="hc8-mr-3x"
              onClick={() =>
                setProductCapabilityDialogSettings({
                  ...productCapabilityDialogSettings,
                  isVisible: true,
                  isReadOnly: true,
                })
              }
              data-testid="rfi3-manage-capabilities-button"
            >
              <Icon
                svg={PencilIcon}
                size="inline"
                className="hc8-mr-1x"
                aria-hidden
              />
              Manage Capabilities
            </Button>
          ) : null}
        </Grid.Item>

        <Grid.Item
          size={{ xs: 12 }}
          data-testid="cards-scroll-container"
          className="lcm-capability-card-strip hc8-pb-2x"
        >
          {/* Total approved */}
          <div className="lcm-capability-card-shell">
            <Card
              variant="elevated"
              className="lcm-capability-summary-card lcm-capability-summary-card--total hc8-pa-4x"
              data-testid="total-approved-card"
            >
              <div className="hc8-fs-150 hc8-clr-disabled hc8-mb-1x">
                Total Approved
              </div>

              <div
                className="hc8-fs-350 lcm-capability-summary-card__count hc8-clr-brand"
                data-testid="total-approved-count"
              >
                {total_capability?.total_approved ?? 0}
              </div>

              <div className="hc8-fs-150 hc8-clr-disabled hc8-mb-1x">
                of {total_capability?.total_capabilities ?? 0}{' '}
                {total_capability?.total_capabilities > 1
                  ? 'capabilities'
                  : 'capability'}
              </div>
            </Card>
          </div>

          {/* Approval type cards */}
          {approved_capability_list?.map((capGroup) => {
            const caps = capGroup.capabilities || []

            const previewLines = caps.slice(0, 2).map(formatCapabilityLine)
            const previewText = previewLines.join(', ')
            const needsTooltip = caps.length > 2 || previewText.length > 40

            const pct =
              percentBase > 0
                ? Math.round((caps.length / percentBase) * 100)
                : 0

            return (
              <Grid.Item
                key={capGroup.approval_type_id ?? capGroup.approval_type_name}
                className="lcm-capability-card-shell"
              >
                <Card
                  variant="elevated"
                  className="lcm-capability-summary-card hc8-pa-4x"
                  data-testid={`approval-card-${capGroup.approval_type_id}`}
                >
                  <div className="hc8-fs-150 hc8-clr-disabled hc8-mb-1x">
                    {capGroup.approval_type_name}
                  </div>

                  <div className="lcm-capability-summary-card__metrics hc8-mb-1x">
                    <div
                      className="hc8-fs-350 lcm-capability-summary-card__count hc8-clr-success"
                      data-testid={`count-${capGroup.approval_type_id}`}
                    >
                      {caps.length || 0}
                    </div>

                    <div className="lcm-capability-summary-card__detail">
                      <div className="hc8-fs-100 hc8-clr-disabled">
                        {pct}% of total
                      </div>

                      {needsTooltip && (
                        <CustomTooltip
                          key={`tooltip-${capGroup.approval_type_id}`}
                          placement="right"
                          title={buildTooltipContent(caps)}
                          arrow
                          component={
                            <Icon
                              className="lcm-capability-summary-card__info-icon hc8-pl-2x hc8-clr-brand"
                              svg={InfoIcon}
                            />
                          }
                        />
                      )}
                    </div>
                  </div>
                </Card>
              </Grid.Item>
            )
          })}
        </Grid.Item>
      </Grid.Container>

      <Drawer
        isOpen={isCapabilityAuditOpen}
        onRequestClose={onCapabilityAuditDrawerClose}
        headingText={<span>Capability Audit Trail</span>}
        size={{ xs: 3 }}
        className="lcm-capability-audit-drawer"
      >
        <CapabilityAuditTrail
          isReadonly={isReadonly}
          vettingType={rfiType}
          supplierId={supplierId}
          locationId={locationId}
          manufacturingTypeId={manufacturingTypeId}
        />
      </Drawer>
    </Card>
  )
}
